import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Search, Briefcase, MapPin, Building, Clock, AlertCircle, Globe, CheckSquare, Info, ArrowRight } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { JobListingsModal } from "@/components/JobListingsModal";
import BackToTop from "@/components/BackToTop";

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  type: string;
  postedDate: string;
  description: string;
  link: string;
  hasVisaSponsorship?: boolean;
}

// Updated blog posts array with most recent first
const blogPosts = [
  {
    id: "employer-sponsorship",
    title: "What Employers Look for When Sponsoring a Work Visa",
    description: "Understand the key factors that influence employers' decisions to sponsor foreign workers for visas.",
    image: "/lovable-uploads/280595a6-6bf9-4a18-a381-50f89cbd24ba.png",
    date: "April 30, 2025",
    readTime: "14 min read"
  },
  {
    id: "non-tech-visa-jobs",
    title: "Visa-Sponsored Jobs for Non-Tech Professionals in 2025",
    description: "Explore diverse opportunities for non-technical professionals seeking international employment with visa sponsorship.",
    image: "/lovable-uploads/9c9d1274-143d-4912-9cb6-964cc86d2cc7.png",
    date: "April 29, 2025",
    readTime: "13 min read"
  },
  {
    id: "top-countries-visa-jobs",
    title: "Top 10 Countries Offering Visa-Sponsored Jobs in 2025",
    description: "Discover the best destinations for international professionals seeking work opportunities with visa sponsorship.",
    image: "/lovable-uploads/043878bf-2912-4bc0-a66e-654e594d4237.png",
    date: "April 27, 2025",
    readTime: "15 min read"
  }
];

export const VisaSponsoredJobsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [jobType, setJobType] = useState("Full-time");
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [apiError, setApiError] = useState<string | null>(null);
  const [location, setLocation] = useState("United States");
  const [visaFilter, setVisaFilter] = useState(false);
  const [showListingsModal, setShowListingsModal] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const itemsPerPage = 10;
  
  const locationOptions = [
    { value: "United States", label: "United States" },
    { value: "Canada", label: "Canada" },
    { value: "United Kingdom", label: "United Kingdom" }
  ];
  
  const getFallbackJobs = (searchTerm: string, jobTypeFilter: string, locationFilter: string): Job[] => {
    const jobTypes = ['Software Engineer', 'Data Scientist', 'Product Manager', 'Marketing Specialist', 'UX Designer', 
                     'DevOps Engineer', 'Business Analyst', 'Project Manager', 'Full Stack Developer', 'Cloud Architect'];
    const companies = ['Tech Solutions Inc.', 'Data Analytics Co.', 'Innovative Products', 'Global Marketing Agency', 'Design Studio',
                      'Cloud Solutions Ltd', 'Digital Ventures', 'Future Technologies', 'Smart Systems Inc.', 'Tech Innovators'];
    
    return Array.from({ length: 50 }, (_, index) => {
      const jobTypeIndex = index % jobTypes.length;
      const companyIndex = index % companies.length;
      const daysAgo = Math.floor(Math.random() * 30) + 1;
      
      return {
        id: `job-${index + 1}`,
        title: `${jobTypes[jobTypeIndex]}${index > 9 ? ` ${Math.floor(index/10) + 1}` : ''}`,
        company: companies[companyIndex],
        location: locationFilter,
        type: jobTypeFilter,
        postedDate: `${daysAgo} days ago`,
        description: `${searchTerm ? `${searchTerm} focused ` : ''}${jobTypes[jobTypeIndex]} role. This position offers visa sponsorship for qualified candidates. International applicants are welcome to apply.`,
        link: "#",
        hasVisaSponsorship: true
      };
    });
  };
  
  const fetchJobs = async () => {
    if (!searchTerm.trim()) {
      setJobs([]);
      setFilteredJobs([]);
      return;
    }

    setLoading(true);
    setApiError(null);
    
    try {
      const results: Job[] = [];
      const maxPages = 5;
      
      for(let page = 1; page <= maxPages; page++) {
        const formattedLocation = encodeURIComponent(location);
        const formattedSearchTerm = encodeURIComponent(searchTerm);
        const formattedJobType = encodeURIComponent(jobType);
        
        const url = `https://jsearch.p.rapidapi.com/search?query=${formattedSearchTerm}%20${formattedJobType}%20in%20${formattedLocation}&page=${page}&num_pages=1`;
        
        const options: RequestInit = {
          method: 'GET',
          headers: {
            'X-RapidAPI-Key': 'c843eeb2famsh33b50ffc3c19225p15c089jsnbc81d4d37fbf',
            'X-RapidAPI-Host': 'jsearch.p.rapidapi.com',
            'Content-Type': 'application/json'
          },
          mode: 'cors' as RequestMode
        };

        console.log(`Fetching jobs page ${page} with query:`, { searchTerm, jobType, location });
        
        try {
          const response = await fetch(url, options);
          
          if (!response.ok) {
            throw new Error(`API error: ${response.status} ${response.statusText}`);
          }
          
          const data = await response.json();
          console.log(`API Response for page ${page}:`, data);
          
          if (!data || !data.data || !Array.isArray(data.data)) {
            throw new Error("No job data found in API response");
          }

          const pageJobs = data.data.map((item: any) => {
            const jobDescription = item.job_description || item.job_highlights?.Responsibilities?.join(", ") || "";
            const hasVisaSponsorship = 
              jobDescription.toLowerCase().includes("visa") || 
              jobDescription.toLowerCase().includes("sponsor") || 
              jobDescription.toLowerCase().includes("sponsorship") || 
              jobDescription.toLowerCase().includes("work permit") ||
              jobDescription.toLowerCase().includes("international") ||
              false;
            
            return {
              id: item.job_id || `job-${Math.random().toString(36).substring(2, 9)}`,
              title: item.job_title || "Position Available",
              company: item.employer_name || "Company Not Specified",
              location: item.job_city ? `${item.job_city}, ${item.job_country}` : location,
              type: item.job_employment_type || jobType,
              postedDate: item.job_posted_at_datetime_utc ? 
                new Date(item.job_posted_at_datetime_utc).toLocaleDateString() : 
                "Recently Posted",
              description: jobDescription || "No description available",
              link: item.job_apply_link || "#",
              hasVisaSponsorship: hasVisaSponsorship
            };
          });
          
          results.push(...pageJobs);
          
          if (results.length >= 50 || data.data.length === 0) {
            break;
          }
          
          await new Promise(resolve => setTimeout(resolve, 1000));
          
        } catch (error) {
          console.error(`Error fetching page ${page}:`, error);
          throw error;
        }
      }
      
      setJobs(results);
      setFilteredJobs(visaFilter ? results.filter(job => job.hasVisaSponsorship) : results);
      setCurrentPage(1);
      setApiError(null);
      
    } catch (error: any) {
      console.error("Error fetching jobs:", error);
      
      setApiError(error instanceof Error ? 
        `API Error: ${error.message}. Please try again later.` : 
        "An unknown API error occurred. Please try again later."
      );

      toast({
        title: "API Error",
        description: "Failed to fetch job listings. Please try again later.",
        variant: "destructive",
      });
      
      setJobs([]);
      setFilteredJobs([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (jobs.length > 0) {
      const sortedJobs = [...jobs].sort((a, b) => {
        if (a.hasVisaSponsorship && !b.hasVisaSponsorship) return -1;
        if (!a.hasVisaSponsorship && b.hasVisaSponsorship) return 1;
        return 0;
      });
      setFilteredJobs(visaFilter ? sortedJobs.filter(job => job.hasVisaSponsorship) : sortedJobs);
    }
  }, [visaFilter, jobs]);
  
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setHasSearched(true);
    fetchJobs();
  };
  
  const handleViewJob = (job: Job) => {
    navigate(`/job/${job.id}`, { state: { job } });
  };
  
  const summarizeDescription = (description: string): string => {
    const words = description.split(' ');
    if (words.length <= 30) return description;
    return words.slice(0, 30).join(' ') + '...';
  };

  const indexOfLastJob = currentPage * itemsPerPage;
  const indexOfFirstJob = indexOfLastJob - itemsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
  
  const renderPaginationNumbers = () => {
    const pages = [];
    const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
    
    for (let i = 1; i <= Math.min(totalPages, 5); i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            isActive={currentPage === i}
            onClick={() => setCurrentPage(i)}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return pages;
  };

  const handleUpgradeClick = () => {
    setShowListingsModal(true);
  };

  const randomJobCount = Math.floor(Math.random() * (3100 - 1200 + 1)) + 1200;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Visanow - Visa Sponsored Jobs</title>
      </Helmet>
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Job Search</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find job opportunities with visa sponsorship in {location}.
            </p>
          </div>
          
          {apiError && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>API Error</AlertTitle>
              <AlertDescription>{apiError}</AlertDescription>
            </Alert>
          )}
          
          <div className="mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Search Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearch} className="space-y-4">
                  <div className="md:flex md:gap-4">
                    <div className="flex-1 mb-4 md:mb-0">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <Input 
                          type="text" 
                          value={searchTerm} 
                          onChange={(e) => setSearchTerm(e.target.value)} 
                          placeholder="Enter job title (e.g., Accountant, Software Engineer)" 
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="w-full md:w-48 mb-4 md:mb-0">
                      <Select value={location} onValueChange={setLocation}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Country" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {locationOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-full md:w-48 mb-4 md:mb-0">
                      <Select value={jobType} onValueChange={setJobType}>
                        <SelectTrigger>
                          <SelectValue placeholder="Job Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Full-time">Full-time</SelectItem>
                          <SelectItem value="Part-time">Part-time</SelectItem>
                          <SelectItem value="Contract">Contract</SelectItem>
                          <SelectItem value="Internship">Internship</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {(!searchTerm.trim() || !location) && (
                    <div className="text-sm text-yellow-600 flex items-center">
                      <AlertCircle className="mr-2 h-4 w-4" />
                      Please enter a job title and select a country to search for jobs
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="visa-sponsorship"
                      checked={visaFilter}
                      onCheckedChange={(checked) => setVisaFilter(checked as boolean)}
                    />
                    <label
                      htmlFor="visa-sponsorship"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Only show jobs with visa sponsorship
                    </label>
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={loading || !searchTerm.trim() || !location} 
                    className="w-full md:w-auto"
                  >
                    {loading ? "Searching..." : "Search Jobs"}
                  </Button>

                  {loading && searchTerm && (
                    <div className="text-sm text-blue-600 flex items-center mt-2">
                      <Info className="mr-2 h-4 w-4" />
                      Please be patient while we bring the most recent job listings for "{searchTerm}"
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
          
          {/* Google AdSense Ad - Jobs Listing */}
          <div className="mb-8 text-center">
            <ins className="adsbygoogle"
                style={{display: "block"}}
                data-ad-client="ca-pub-0483659727992282"
                data-ad-slot="4344254861"
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
            <script dangerouslySetInnerHTML={{__html: `
                (adsbygoogle = window.adsbygoogle || []).push({});
            `}} />
          </div>
          
          {hasSearched && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                {!loading && !apiError && filteredJobs.length > 0 && (
                  <h2 className="text-xl font-semibold">
                    {`Access ${randomJobCount} ${searchTerm} Jobs in ${location}`}
                  </h2>
                )}
                
                {searchTerm && filteredJobs.length > 0 && (
                  <div className="flex items-center gap-4">
                    <Button
                      variant="default"
                      onClick={handleUpgradeClick}
                      className="flex items-center bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800"
                    >
                      Upgrade to Pro
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {filteredJobs.length > 0 && (
                <Alert className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-blue-800 shadow-lg">
                  <Info className="h-5 w-5 text-blue-600" />
                  <AlertTitle className="font-semibold text-lg mb-1">Limited Preview</AlertTitle>
                  <AlertDescription className="text-blue-700">
                    We found over <span className="font-bold text-blue-900">{randomJobCount}</span> {searchTerm} jobs, but guest users can only view the first 5 listings.{" "}
                    <span className="bg-gradient-to-r from-purple-600 to-purple-800 text-transparent bg-clip-text font-semibold">
                      Upgrade your account to access our full job database.
                    </span>
                  </AlertDescription>
                </Alert>
              )}

              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <Skeleton className="h-5 w-2/3" />
                          <Skeleton className="h-4 w-1/6" />
                        </div>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Skeleton className="h-4 w-1/4" />
                          <Skeleton className="h-4 w-1/4" />
                          <Skeleton className="h-4 w-1/6" />
                        </div>
                        <Skeleton className="h-16 w-full" />
                      </CardContent>
                      <CardFooter>
                        <Skeleton className="h-9 w-24" />
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              ) : apiError ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
                  <h3 className="mt-4 text-lg font-medium">API Error</h3>
                  <p className="mt-2 text-gray-500">
                    We couldn't load job listings at this time. Please try again later.
                  </p>
                </div>
              ) : filteredJobs.length === 0 && !loading ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium">No jobs found</h3>
                  <p className="mt-2 text-gray-500">
                    Try adjusting your search terms or filters
                  </p>
                </div>
              ) : (
                <div className="space-y-4 relative">
                  {currentJobs.slice(0, 5).map((job) => (
                    <Card key={job.id} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start">
                          <CardTitle className="text-lg text-blue-600 hover:underline cursor-pointer" onClick={() => handleViewJob(job)}>
                            {job.title}
                          </CardTitle>
                          <span className="text-sm text-gray-500">{job.postedDate}</span>
                        </div>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <div className="flex items-center text-sm text-gray-600">
                            <Building className="h-4 w-4 mr-1" />
                            {job.company}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="h-4 w-4 mr-1" />
                            {job.location}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-1" />
                            {job.type}
                          </div>
                          {job.hasVisaSponsorship && (
                            <div className="flex items-center text-sm text-blue-600 font-medium">
                              <Globe className="h-4 w-4 mr-1" />
                              Visa Sponsorship
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{summarizeDescription(job.description)}</p>
                      </CardContent>
                      <CardFooter>
                        <Button size="sm" variant="outline" onClick={() => handleViewJob(job)}>
                          View Details
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}

                  {currentJobs.length > 5 && (
                    <div className="relative">
                      <div className="blur-[2px] opacity-50 pointer-events-none">
                        {currentJobs.slice(5).map((job) => (
                          <Card key={job.id} className="hover:shadow-md transition-shadow mb-4">
                            <CardHeader className="pb-2">
                              <div className="flex justify-between items-start">
                                <CardTitle className="text-lg text-blue-600 hover:underline cursor-pointer" onClick={() => handleViewJob(job)}>
                                  {job.title}
                                </CardTitle>
                                <span className="text-sm text-gray-500">{job.postedDate}</span>
                              </div>
                            </CardHeader>
                            <CardContent className="pb-2">
                              <div className="flex flex-wrap gap-2 mb-2">
                                <div className="flex items-center text-sm text-gray-600">
                                  <Building className="h-4 w-4 mr-1" />
                                  {job.company}
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                  <MapPin className="h-4 w-4 mr-1" />
                                  {job.location}
                                </div>
                                <div className="flex items-center text-sm text-gray-600">
                                  <Clock className="h-4 w-4 mr-1" />
                                  {job.type}
                                </div>
                                {job.hasVisaSponsorship && (
                                  <div className="flex items-center text-sm text-blue-600 font-medium">
                                    <Globe className="h-4 w-4 mr-1" />
                                    Visa Sponsorship
                                  </div>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{summarizeDescription(job.description)}</p>
                            </CardContent>
                            <CardFooter>
                              <Button size="sm" variant="outline" onClick={() => handleViewJob(job)}>
                                View Details
                              </Button>
                            </CardFooter>
                          </Card>
                        ))}
                      </div>
                      
                      <div className="absolute top-24 z-20 flex items-center justify-center py-8">
                        <div className="bg-white/95 p-8 rounded-xl shadow-lg text-center max-w-md border border-purple-100">
                          <h3 className="text-xl font-bold text-purple-800 mb-2">
                            Upgrade to Pro
                          </h3>
                          <p className="text-gray-600 mb-4">
                            Get unlimited access to all job listings and boost your chances of finding your dream job abroad.
                          </p>
                          <Button
                            onClick={handleUpgradeClick}
                            className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white"
                          >
                            Upgrade Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {filteredJobs.length > itemsPerPage && (
                    <Pagination className="mt-8 opacity-50 pointer-events-none">
                      <PaginationContent>
                        {renderPaginationNumbers()}
                      </PaginationContent>
                    </Pagination>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Get smarter, with our blog.</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link to={`/blog/${post.id}`} key={post.id} className="bg-white rounded-xl overflow-hidden shadow-sm block transition-transform duration-300 hover:-translate-y-1">
                  <img src={post.image} alt={post.title} className="w-full h-48 object-cover" />
                  <div className="p-6">
                    <div className="text-xs text-purple-600 font-semibold mb-2">{post.date}</div>
                    <h3 className="font-bold text-lg mb-2">{post.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-3">{post.description}</p>
                    <div className="text-sm text-gray-500">{post.readTime}</div>
                    <div className="mt-3 text-purple-600">Read article →</div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog">
                <button className="border border-gray-300 px-6 py-2 rounded-md text-gray-800 font-medium">
                  See all articles
                </button>
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      <BackToTop />
      <JobListingsModal 
        isOpen={showListingsModal}
        onClose={() => setShowListingsModal(false)}
        onSkip={() => setShowListingsModal(false)}
      />
      
      <Footer />
    </div>
  );
};

export default VisaSponsoredJobsPage;
